// 文献管理服务 - 适配实验室管理系统的API调用方式
const API_BASE = '/api';

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

/** 带 HTTP 状态与响应体的请求错误，便于前端做分类提示 */
export type ApiRequestError = Error & {
  status?: number;
  body?: Record<string, unknown>;
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      const err401 = new Error('未授权，请重新登录') as ApiRequestError;
      err401.status = 401;
      throw err401;
    }
    const error = (await response.json().catch(() => ({ message: '请求失败' }))) as Record<string, unknown>;
    const msg =
      (typeof error.message === 'string' && error.message) ||
      (typeof error.error === 'string' && error.error) ||
      '请求失败';
    const err = new Error(msg) as ApiRequestError;
    err.status = response.status;
    err.body = error;
    throw err;
  }

  return response.json();
};

export interface LiteratureSummary {
  researchQuestion: string;
  researchProcess: string;
  researchResults: string;
  innovations: string;
  analyzedAt: string | null;
  analyzing: boolean;
}

/** 相关文献简要（详情接口在 relatedLiteratureIds 之外附带，便于展示） */
export interface RelatedLiteratureBrief {
  _id: string;
  title: string;
  year?: number;
  journal?: string;
  authors?: string[];
}

/** 文献附件：文件（占存储）或外部链接 */
export interface LiteratureAttachment {
  _id?: string | null;
  type: 'file' | 'link';
  name: string;
  url: string;
  mimeType?: string;
  fileSize?: number;
  fileHash?: string;
  addedAt?: string;
}

export interface Literature {
  _id: string;
  title: string;
  authors: string[];
  /** 期刊（存储字段 journal，与 CSL container-title 等出版物容器题名对齐） */
  journal?: string;
  year?: number;
  doi?: string;
  pmid?: string;
  /** 期刊/出版社文章页等外部网址（书目信息，非附件） */
  articleUrl?: string;
  /** RIS/BibTeX 常见扩展字段 */
  volume?: string;
  issue?: string;
  pages?: string;
  publisher?: string;
  issn?: string;
  isbn?: string;
  /** 书目语言（接口字段 docLanguage，避免与保留字冲突） */
  docLanguage?: string;
  abstract?: string;
  keywords?: string[];
  pdfUrl?: string;
  pdfFileName?: string;
  /** 合并附件后的首选 PDF 地址（接口计算字段） */
  primaryPdfUrl?: string;
  attachments?: LiteratureAttachment[];
  category?: string;
  categoryId?: string;
  tags?: string[];
  notes?: string;
  /** 相关文献 id（详情接口为字符串 id 列表） */
  relatedLiteratureIds?: string[];
  /** 相关文献展示用（仅详情接口） */
  relatedLiteratures?: RelatedLiteratureBrief[];
  rating?: number;
  readStatus: '未读' | '阅读中' | '已读' | '精读';
  isFavorited: boolean;
  citations?: number;
  impactFactor?: number;
  summary?: LiteratureSummary;
  uploadedBy?: {
    _id: string;
    username: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LiteratureLinkAttachmentInput {
  name?: string;
  url: string;
}

export interface LiteratureFormData {
  title: string;
  authors?: string[];
  /** 期刊（与 CSL container-title 对齐） */
  journal?: string;
  year?: number;
  doi?: string;
  pmid?: string;
  articleUrl?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  publisher?: string;
  issn?: string;
  isbn?: string;
  /** 书目语言（接口字段 docLanguage，避免与保留字冲突） */
  docLanguage?: string;
  abstract?: string;
  keywords?: string[];
  category?: string;
  categoryId?: string;
  tags?: string[];
  notes?: string;
  /** 相关文献 id 列表；保存时与同账号下条目双向关联 */
  relatedLiteratureIds?: string[];
  readStatus?: '未读' | '阅读中' | '已读' | '精读';
  rating?: number;
  pdf?: File;
  /** @deprecated 创建页已改为书目字段 articleUrl；保留以兼容旧调用 */
  attachmentLinks?: LiteratureLinkAttachmentInput[];
}

export interface LiteratureListParams {
  page?: number;
  limit?: number;
  keyword?: string;
  year?: number;
  journal?: string;
  category?: string;
  categoryId?: string;
  readStatus?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** 判断附件是否为 PDF 文件（用于列表展示与打开） */
export function isPdfFileAttachment(a: LiteratureAttachment): boolean {
  if (a.type !== 'file' || !a.url?.trim()) return false;
  const mime = (a.mimeType || '').toLowerCase();
  const name = (a.name || '').toLowerCase();
  const url = (a.url || '').toLowerCase();
  return mime === 'application/pdf' || name.endsWith('.pdf') || url.includes('.pdf');
}

function normalizeAttachmentUrlForCompare(u: string): string {
  if (!u || typeof u !== 'string') return '';
  let s = u.trim().replace(/\/+$/, '');
  try {
    s = decodeURIComponent(s);
  } catch {
    /* ignore */
  }
  return s;
}

/**
 * 展示用附件列表：与后端 serializeLiterature 一致，将仅有 legacy pdfUrl 的主 PDF 并入列表。
 */
export function mergeAttachmentsForDisplay(
  lit: Pick<Literature, 'attachments' | 'pdfUrl' | 'pdfFileName'> | null | undefined
): LiteratureAttachment[] {
  if (!lit) return [];
  const list = [...(lit.attachments || [])];
  const p = (lit.pdfUrl || '').trim();
  if (!p) return list;
  const target = normalizeAttachmentUrlForCompare(p);
  const has = list.some(
    (a) => a.type === 'file' && a.url && normalizeAttachmentUrlForCompare(a.url) === target
  );
  if (has) return list;
  return [
    {
      _id: 'legacy-primary-pdf',
      type: 'file',
      name: (lit.pdfFileName && lit.pdfFileName.trim()) || '主 PDF',
      url: p,
      mimeType: 'application/pdf'
    },
    ...list
  ];
}

/** 附件表行是否为主 PDF（与 mergeAttachmentsForDisplay / primaryPdfUrl 语义一致） */
export function isPrimaryPdfAttachmentRow(
  lit: Pick<Literature, 'pdfUrl' | 'primaryPdfUrl'> | null | undefined,
  row: LiteratureAttachment
): boolean {
  if (String(row._id) === 'legacy-primary-pdf') return true;
  if (row.type !== 'file' || !row.url?.trim()) return false;
  const nu = normalizeAttachmentUrlForCompare(row.url);
  const p = (lit?.pdfUrl || '').trim();
  const pp = (lit?.primaryPdfUrl || '').trim();
  if (p && normalizeAttachmentUrlForCompare(p) === nu) return true;
  if (pp && normalizeAttachmentUrlForCompare(pp) === nu) return true;
  return false;
}

/** 从附件中列出所有 PDF 文件（含接口合并进来的 legacy 主 PDF）；用于选择与阅读器 */
export function listLiteraturePdfAttachments(
  lit: Pick<Literature, 'attachments' | 'pdfUrl' | 'pdfFileName'> | null | undefined
): Array<{ id: string; name: string; url: string }> {
  const merged = mergeAttachmentsForDisplay(lit);
  if (!merged.length) return [];
  const out: Array<{ id: string; name: string; url: string }> = [];
  for (const a of merged) {
    if (!isPdfFileAttachment(a)) continue;
    const id =
      a._id != null && String(a._id) !== ''
        ? String(a._id)
        : `u:${encodeURIComponent(a.url)}`;
    out.push({ id, name: (a.name && a.name.trim()) || 'PDF', url: a.url.trim() });
  }
  return out;
}

/** 是否存在可打开的 PDF（附件中有 PDF，或仅有 legacy pdfUrl） */
export function literatureHasPdfAttachment(
  lit: Pick<Literature, 'attachments' | 'primaryPdfUrl' | 'pdfUrl'> | null | undefined
): boolean {
  if (!lit) return false;
  if (listLiteraturePdfAttachments(lit).length > 0) return true;
  return !!(lit.primaryPdfUrl?.trim() || lit.pdfUrl?.trim());
}

/** 共享文献列表等：除正文库字段外，将同步快照中的 pdfUrl 视为可用 PDF（与后端 SharedLiterature.snapshot 一致） */
export function literatureHasPdfIncludingSnapshot(
  lit:
    | (Pick<Literature, 'attachments' | 'primaryPdfUrl' | 'pdfUrl' | 'pdfFileName'> & {
        snapshot?: { pdfUrl?: string | null } | null;
      })
    | null
    | undefined
): boolean {
  if (literatureHasPdfAttachment(lit)) return true;
  const u = lit?.snapshot?.pdfUrl;
  return !!(u && String(u).trim());
}

/**
 * 默认用于列表/按钮：取第一个 PDF 附件的 URL；若无附件列表则回退 primaryPdfUrl/pdfUrl
 */
export function getLiteraturePdfUrl(
  lit: Pick<Literature, 'primaryPdfUrl' | 'pdfUrl' | 'attachments'> | null | undefined
): string {
  if (!lit) return '';
  const list = listLiteraturePdfAttachments(lit);
  if (list.length > 0) return list[0].url;
  return (lit.primaryPdfUrl && lit.primaryPdfUrl.trim()) || (lit.pdfUrl && lit.pdfUrl.trim()) || '';
}

/**
 * 按附件 id（或查询参数）解析要打开的 PDF URL；多附件时需传入 attachmentId
 */
export function resolveLiteraturePdfUrlByAttachmentId(
  lit: Pick<Literature, 'primaryPdfUrl' | 'pdfUrl' | 'attachments'> | null | undefined,
  attachmentId: string | null | undefined
): string {
  if (!lit) return '';
  const list = listLiteraturePdfAttachments(lit);
  if (list.length === 0) return getLiteraturePdfUrl(lit);
  if (list.length === 1) return list[0].url;
  if (attachmentId) {
    const found = list.find((p) => p.id === attachmentId);
    if (found) return found.url;
  }
  return list[0].url;
}

/** 文献存储用量（有团队时为团队合计，否则为个人） */
export interface LiteratureStorageSummary {
  scope: 'team' | 'personal';
  quotaBytes: number;
  usedBytes: number;
  teamName: string | null;
}

/** RIS / BibTeX 解析预览单条（与后端 bibliographyImport 字段一致） */
export interface BibliographyImportEntryPreview {
  title: string;
  authors: string[];
  /** 界面「期刊」；后端从 CSL container-title、journal、booktitle、event-title 等归并 */
  journal?: string;
  year?: number;
  doi?: string;
  pmid?: string;
  abstract?: string;
  articleUrl?: string;
  keywords?: string[];
  volume?: string;
  issue?: string;
  pages?: string;
  publisher?: string;
  issn?: string;
  isbn?: string;
  docLanguage?: string;
}

export interface BibliographyImportPreviewResult {
  format: string;
  entries: BibliographyImportEntryPreview[];
}

export interface BibliographyImportCommitResult {
  createdCount: number;
  errorCount: number;
  items: Literature[];
  errors: Array<{ index: number; message: string }>;
}

export type LiteratureExportStyle = 'gb7714-numeric' | 'apa' | 'vancouver' | 'harvard1';

export interface LiteratureExportCslData {
  text: string;
  html: string;
  style: string;
  count: number;
}

/** HTTP 或非用户手势等场景下 navigator.clipboard 可能不存在或不可用 */
function copyPlainTextWithExecCommand(text: string): void {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    const ok = document.execCommand('copy');
    if (!ok) throw new Error('execCommand copy failed');
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * 无 Clipboard API 时，用选中富文本 + execCommand('copy') 写入剪贴板。
 * Chrome/Edge 下粘贴到 Word 常可保留 CSL 的 i/b 等样式；纯 textarea 只能复制纯文本。
 * （HTTPS 下仍优先使用 clipboard.write + ClipboardItem，兼容性更好。）
 */
function copyRichHtmlWithExecCommand(htmlFragment: string): boolean {
  const wrap = document.createElement('div');
  wrap.innerHTML = htmlFragment;
  wrap.setAttribute('tabindex', '-1');
  wrap.style.position = 'fixed';
  wrap.style.left = '-10000px';
  wrap.style.top = '0';
  wrap.style.width = 'max-content';
  wrap.style.maxWidth = '900px';
  wrap.style.opacity = '0';
  wrap.style.pointerEvents = 'none';
  document.body.appendChild(wrap);
  wrap.focus();

  const sel = window.getSelection();
  if (!sel) {
    document.body.removeChild(wrap);
    return false;
  }
  sel.removeAllRanges();
  const range = document.createRange();
  range.selectNodeContents(wrap);
  sel.addRange(range);

  let ok = false;
  try {
    ok = document.execCommand('copy');
  } finally {
    sel.removeAllRanges();
    document.body.removeChild(wrap);
  }
  return ok;
}

async function writePlainToClipboard(plain: string): Promise<void> {
  if (typeof navigator.clipboard?.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(plain);
      return;
    } catch {
      /* NotAllowedError 等 */
    }
  }
  copyPlainTextWithExecCommand(plain);
}

/**
 * 将 CSL 导出写入剪贴板：同时提供 text/html 与 text/plain。
 * 仅用 writeText(plain) 时 Word 只会收到纯文本，期刊名斜体等会丢失；
 * 多 MIME 写入后粘贴到 Word / Pages 等可保留样式。
 * 非 HTTPS 或 write 失败时，先尝试选中 HTML 的 execCommand 复制以保留 Word 中的斜体等，再退回纯文本。
 */
export async function copyLiteratureCslExportToClipboard(data: LiteratureExportCslData): Promise<void> {
  const plain = String(data.text ?? '');
  const fragment = String(data.html ?? '').trim();

  if (!fragment) {
    await writePlainToClipboard(plain);
    return;
  }

  const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><!--StartFragment-->${fragment}<!--EndFragment--></body></html>`;

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlDoc], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' })
        })
      ]);
      return;
    } catch {
      /* 部分环境不支持多类型写入 */
    }
  }

  try {
    if (copyRichHtmlWithExecCommand(fragment)) return;
  } catch {
    /* Safari 等可能抛错 */
  }

  await writePlainToClipboard(plain);
}

/** 用户上传的 CSL 样式（用于导出引用） */
export interface UserCslStyleInfo {
  styleKey: string;
  label: string;
  createdAt: string;
}

/** 官方 citation-style-language/styles 仓库中的条目 */
export interface CslCatalogItem {
  path: string;
  label: string;
}

export interface CslCatalogResult {
  items: CslCatalogItem[];
  total: number;
  truncated: boolean;
  /** bundle=本地同步包 | github | github-cache | seed */
  source: string;
  fetchedAt: number;
}

/** 下载 BibTeX / RIS（附件响应） */
async function postLiteraturesExportBlob(
  ids: string[],
  format: 'bibtex' | 'ris',
  includeAbstract = true
): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
  const response = await fetch(`${API_BASE}/literatures/export`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ids, format, includeAbstract })
  });
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const err = await response.json().catch(() => ({ message: '导出失败' }));
    throw new Error(
      (err as { message?: string }).message || (err as { error?: string }).error || '导出失败'
    );
  }
  const blob = await response.blob();
  const cd = response.headers.get('Content-Disposition');
  let filename = format === 'bibtex' ? 'literatures.bib' : 'literatures.ris';
  if (cd) {
    const m = /filename="([^"]+)"/.exec(cd);
    if (m) [, filename] = m;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const literatureService = {
  /** 当前登录用户可见的文献库存储量（团队 2GB 为全团队共用） */
  getStorageSummary: async (): Promise<LiteratureStorageSummary> => {
    const response = await apiRequest('/literatures/team-storage');
    return response.data;
  },

  /** 解析 RIS 或 BibTeX 文本，返回映射后的条目（不落库） */
  importPreview: async (
    text: string,
    format?: 'auto' | 'bibtex' | 'ris'
  ): Promise<BibliographyImportPreviewResult> => {
    const response = await apiRequest('/literature-bib/preview', {
      method: 'POST',
      body: JSON.stringify({ text, format })
    });
    return response.data;
  },

  /** 批量写入解析后的条目 */
  importCommit: async (
    entries: BibliographyImportEntryPreview[],
    categoryId?: string
  ): Promise<BibliographyImportCommitResult> => {
    const response = await apiRequest('/literature-bib/commit', {
      method: 'POST',
      body: JSON.stringify({ entries, categoryId })
    });
    return response.data;
  },

  // 获取文献列表
  getList: async (params: LiteratureListParams = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key as keyof LiteratureListParams];
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const response = await apiRequest(`/literatures?${queryParams.toString()}`);
    return response.data;
  },

  /** 导出 BibTeX / RIS 文件（勾选或传入的 id 列表） */
  exportLiteraturesFile: async (
    ids: string[],
    format: 'bibtex' | 'ris',
    includeAbstract = true
  ): Promise<void> => {
    await postLiteraturesExportBlob(ids, format, includeAbstract);
  },

  /**
   * 按 CSL 样式生成参考文献纯文本与 HTML（用于复制或 Word 加载项）。
   * `style` 可为内置 gb7714-numeric / apa / vancouver / harvard1，或上传接口返回的 `cus_…` styleKey。
   */
  exportLiteraturesCsl: async (
    ids: string[],
    style: LiteratureExportStyle | string = 'gb7714-numeric',
    includeAbstract = true,
    cslLocale?: string
  ): Promise<LiteratureExportCslData> => {
    const body: Record<string, unknown> = {
      ids,
      format: 'csl',
      style,
      includeAbstract
    };
    if (cslLocale && String(cslLocale).trim()) {
      body.cslLocale = String(cslLocale).trim();
    }
    const response = await apiRequest('/literatures/export', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return response.data as LiteratureExportCslData;
  },

  /** 列出当前用户已上传的自定义 CSL */
  listUserCslStyles: async (): Promise<UserCslStyleInfo[]> => {
    const response = await apiRequest('/literatures/csl-styles');
    return (response.data || []) as UserCslStyleInfo[];
  },

  /** 上传 .csl 文件；返回 styleKey 供导出时作为 style 传入 */
  uploadUserCslStyle: async (
    file: File,
    label?: string
  ): Promise<{ styleKey: string; label: string }> => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    if (label && label.trim()) {
      fd.append('label', label.trim());
    }
    const response = await fetch(`${API_BASE}/literatures/csl-styles`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd
    });
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/';
        throw new Error('未授权，请重新登录');
      }
      const err = await response.json().catch(() => ({ message: '上传失败' }));
      throw new Error(
        (err as { message?: string }).message || (err as { error?: string }).error || '上传失败'
      );
    }
    const json = await response.json();
    return json.data as { styleKey: string; label: string };
  },

  deleteUserCslStyle: async (styleKey: string): Promise<void> => {
    await apiRequest(`/literatures/csl-styles/${encodeURIComponent(styleKey)}`, {
      method: 'DELETE'
    });
  },

  /**
   * 根据 DOI 从开放获取 / Crossref / 服务端配置的解析链拉取全文 PDF 与支撑材料并写入附件。
   * Unpaywall 使用当前登录用户在系统中的联系邮箱（个人资料中的邮箱）。
   */
  fetchFulltextFromDoi: async (
    literatureId: string,
    options?: { skipPrimaryIfHasPdf?: boolean }
  ): Promise<{
    literature: Literature;
    saved: Array<{ name: string; role: string; source: string }>;
    tried: string[];
    errors: string[];
  }> => {
    const body: Record<string, unknown> = {};
    if (options?.skipPrimaryIfHasPdf === false) {
      body.skipPrimaryIfHasPdf = false;
    }
    const json = (await apiRequest(`/literatures/fetch-fulltext/${encodeURIComponent(literatureId)}`, {
      method: 'POST',
      body: JSON.stringify(body)
    })) as {
      success?: boolean;
      data: {
        literature: Literature;
        saved: Array<{ name: string; role: string; source: string }>;
        tried: string[];
        errors: string[];
      };
    };
    return json.data;
  },

  /** 搜索官方 styles 仓库中的 .csl（服务端缓存 GitHub 目录，失败时用内置短列表） */
  searchCslCatalog: async (q = '', limit = 40, offset = 0): Promise<CslCatalogResult> => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const response = await apiRequest(`/literatures/csl-catalog?${params.toString()}`);
    return response.data as CslCatalogResult;
  },

  /** 从官方库路径拉取并保存为当前用户的自定义样式 */
  importCslFromCatalog: async (path: string): Promise<{ styleKey: string; label: string }> => {
    const response = await apiRequest('/literatures/csl-catalog/import', {
      method: 'POST',
      body: JSON.stringify({ path: String(path || '').trim() })
    });
    return response.data as { styleKey: string; label: string };
  },

  // 获取文献详情
  getDetail: async (id: string) => {
    const response = await apiRequest(`/literatures/${id}`);
    return response.data.literature;
  },

  // 创建文献
  create: async (data: LiteratureFormData) => {
    const formData = new FormData();
    const { attachmentLinks, ...rest } = data;

    Object.keys(rest).forEach((key) => {
      const value = rest[key as keyof typeof rest];
      if (value !== undefined && value !== null) {
        if (key === 'pdf' && value instanceof File) {
          formData.append('pdf', value);
        } else if (Array.isArray(value)) {
          formData.append(key, value.join(','));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (attachmentLinks && attachmentLinks.length > 0) {
      formData.append('attachmentLinks', JSON.stringify(attachmentLinks));
    }

    const token = getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/literatures`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '创建失败' }));
      throw new Error(error.message || '创建失败');
    }

    const result = await response.json();
    return result.data.literature;
  },

  // 更新文献
  update: async (id: string, data: Partial<LiteratureFormData>) => {
    const formData = new FormData();
    const { pdf, attachmentLinks, ...rest } = data;

    Object.keys(rest).forEach((key) => {
      const value = rest[key as keyof typeof rest];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formData.append(key, value.join(','));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (pdf instanceof File) {
      formData.append('pdf', pdf);
    }
    if (attachmentLinks && attachmentLinks.length > 0) {
      formData.append('attachmentLinks', JSON.stringify(attachmentLinks));
    }

    const token = getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/literatures/${id}`, {
      method: 'PUT',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '更新失败' }));
      throw new Error(error.message || '更新失败');
    }

    const result = await response.json();
    return result.data.literature;
  },

  // 删除文献
  delete: async (id: string) => {
    await apiRequest(`/literatures/${id}`, { method: 'DELETE' });
  },

  // 搜索文献
  search: async (keyword: string, limit: number = 20) => {
    const response = await apiRequest(`/literatures/search?keyword=${encodeURIComponent(keyword)}&limit=${limit}`);
    return response.data.literatures;
  },

  // 提取PDF信息
  extractPDF: async (file: File) => {
    const formData = new FormData();
    formData.append('pdf', file);
    
    const token = getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/literatures/extract-pdf`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '提取失败' }));
      throw new Error(error.message || '提取失败');
    }

    const result = await response.json();
    return result.data;
  },

  // 批量更新分类
  batchUpdateCategory: async (literatureIds: string[], categoryId: string | null) => {
    const response = await apiRequest('/literatures/batch/update-category', {
      method: 'PUT',
      body: JSON.stringify({ literatureIds, categoryId }),
    });
    return response.data;
  },

  /** 添加附件：上传文件（multipart field: file）或 JSON 添加链接 */
  addAttachment: async (
    literatureId: string,
    opts: { file?: File; linkUrl?: string; linkName?: string }
  ): Promise<Literature> => {
    const token = getToken();
    const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    if (opts.linkUrl && !opts.file) {
      const response = await fetch(`${API_BASE}/literatures/${literatureId}/attachments`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          linkUrl: opts.linkUrl.trim(),
          ...(opts.linkName?.trim() ? { linkName: opts.linkName.trim() } : {})
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: '添加失败' }));
        throw new Error(
          (error as { message?: string; error?: string }).message ||
            (error as { message?: string; error?: string }).error ||
            '添加失败'
        );
      }
      const result = await response.json();
      if (!result.data?.literature) {
        throw new Error('接口未返回文献数据');
      }
      return result.data.literature;
    }

    const formData = new FormData();
    if (opts.file) {
      formData.append('file', opts.file);
    }

    const response = await fetch(`${API_BASE}/literatures/${literatureId}/attachments`, {
      method: 'POST',
      headers: authHeaders,
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '添加失败' }));
      throw new Error(
        (error as { message?: string; error?: string }).message ||
          (error as { message?: string; error?: string }).error ||
          '添加失败'
      );
    }
    const result = await response.json();
    if (!result.data?.literature) {
      throw new Error('接口未返回文献数据');
    }
    return result.data.literature;
  },

  removeAttachment: async (literatureId: string, attachmentId: string): Promise<Literature> => {
    const response = await apiRequest(`/literatures/${literatureId}/attachments/${attachmentId}`, {
      method: 'DELETE'
    });
    return response.data.literature;
  }
};
